// @ts-expect-error - TS2614 - Module '"src/api-parsers"' has no exported member 'Workflow'. Did you mean to use 'import Workflow from "src/api-parsers"' instead? | TS2614 - Module '"src/api-parsers"' has no exported member 'Event'. Did you mean to use 'import Event from "src/api-parsers"' instead?
import type {Workflow, Event} from 'src/api-parsers';
import type {RouteLocation, Params} from 'src/types/router';

import type {AtsEntity, EntityType} from 'src/types/ats-entities';
import type {AllowedTearsheetTypes} from 'src/types/ats-settings';

import * as React from 'react';
import {connect, useDispatch, useSelector} from 'react-redux';
import {Link} from 'src/rerouter';
import {EDIT_PERMISSIONS} from 'src/action-creators/roles';
import useDisplayConfig, {useJ2Enabled} from 'src/hooks/useDisplayConfig';
import {useApi} from 'src/hooks/useApi';
import {classify, titleCase} from 'src/utils';
import {hasArnieEntityCredGroup} from 'src/utils/data-integration';
import {
  useReleaseFlag,
  useAnalyticsV2,
  useAudienceFilterSeparation,
  useTRMEnabled,
  useSchedulerHolidyManagement,
  useLiveAgentTransfer,
} from 'src/hooks/product-flags';
import {hasPrivilege} from 'src/utils/accounts';
import useAgencyConfig, {
  useChatbotEnabled,
  useSchedulerEnabled,
  useJobMatchEnabledForAnyProduct,
  useSenseEvents,
  useVoiceAiMarketingPageEnabled,
  useAutomationSurvey,
} from 'src/hooks/useAgencyConfig';
import usePrivilege from 'src/hooks/usePrivilege';
import useReferrals from 'src/hooks/useReferrals';
import useI18n from 'src/hooks/useI18n';

import {getAllowedTearsheetTypes} from 'src/action-creators/ats-settings';
import {toggleSideNav} from 'src/action-creators/navigation';
import {
  selectDNSV2Enabled,
  selectAgencyConfigFlag,
  selectAgencyConfig,
  selectEnableChatbot,
} from 'src/selectors/agency';
import {canUseChatPreRelease} from 'src/selectors/chat';
import {
  selectCredentialsMetaData,
  selectCredentials,
} from 'src/selectors/data-integration';

import {getAtsEntityMappings} from 'src/selectors/ats-entities';
import {selectCorporateLabels} from 'src/selectors/language-support';

import Pusher from 'src/components/navigation/pusher/pusher';
import {
  AnalyticsWorkflowList,
  DefaultWorkflowList,
} from 'src/components/navigation/workflow-detail';
import ScrollView from 'src/components/lib/scroll-view';
import Loading from 'src/components/lib/loading/loading';
import TruncatedText from 'src/components/lib/truncated-text';
import TopNavLink from 'src/components/navigation/top-nav-link';
import MessagesNavLink from 'src/components/messaging/messages-nav-link';
import ZendeskTrigger from 'src/components/lib/zendesk-trigger';
import {isInternalUser} from 'src/utils/check-internal-user';
import {
  ANALYTICS_API_V3,
  checkJdGeneratorEnabled,
  checkAnalyticsNewLandingPageEnabled,
} from 'src/utils/analytics-api-migration';

import ArrowLeft from 'src/images/arrow-left.svg';
import AnalyticsEngagementCohortsDetail from './analytics-engagement-cohorts-detail';

import MetricsIcon from 'src/images/nav/metrics-icon.svg?noAttrs';
import ResponsesIcon from 'src/images/nav/responses-icon.svg';
import FlagIcon from 'src/images/flag-icon.svg?noAttrs';
import AtsFieldsIcon from 'src/images/ats-settings.svg?noAttrs';
import EngagementIcon from 'src/images/nav/engagement-icon.svg?noAttrs';
import ClicksIcon from 'src/images/nav/clicks-icon.svg?noAttrs';
import NpsIcon from 'src/images/nav/nps.svg?noAttrs';
import PerformanceIcon from 'src/images/nav/performance-icon.svg?noAttrs';
import JobMatchesIcon from 'src/images/nav/job_matches.svg?noAttrs';
import ChatbotAnalyticsIcon from 'src/images/nav/chatbot-analytics-icon.svg?noAttrs';
import ChatbotTranscriptsIcon from 'src/images/nav/chatbot-transcripts-icon.svg?noAttrs';
import MessagesAnalyticsIcon from 'src/images/nav/messages-analytics-icon.svg?noAttrs';

import TemplateIcon from 'src/images/template-icon.svg?noAttrs';
import SmartphoneIcon from 'src/images/icons/smartphone-icon.svg?noAttrs';
import GlobalVariableIcon from 'src/images/global-variable-icon.svg?noAttrs';
import PhoneIcon from 'src/images/phone-icon.svg?noAttrs';
import EmailIcon from 'src/images/email-icon.svg?noAttrs';
import ReportsIcon from 'src/images/icons/reports-icon.svg?noAttrs';
import ReferralGlyphIcon from 'src/images/icons/referral-glyph-icon.svg?noAttrs';
import DeliveryHistoryIcon from 'src/images/icons/delivery-history-icon.svg?noAttrs';

import ChatbotIcon from 'src/images/nav/chatbot.svg';
import DashboardIcon from 'src/images/icons/dashboard-nav-icon.svg';
import JourneysIcon from 'src/images/icons/journeys-nav-icon.svg';
import SurveysIcon from 'src/images/icons/survey-nav-icon.svg';
import AiRecruiterIcon from 'src/images/icons/ai-recruiter-icon.svg';
import CleanupIcon from 'src/images/icons/cleanup-nav-icon.svg';
import AnalyticsIcon from 'src/images/icons/analytics-nav-icon.svg';
import AuditIcon from 'src/images/icons/audit-nav-icon.svg';
import ScheduleIcon from 'src/images/icons/schedule-nav-icon.svg';
import PeopleIcon from 'src/images/icons/people-nav-icon.svg';
import DataManagementIcon from 'src/images/icons/database.svg';
import JobsIcon from 'src/images/icons/jobs-nav-icon.svg';
import TrmIcon from 'src/images/icons/trm-nav-icon.svg';
import MessagesIcon from 'src/images/icons/messages-nav-icon.svg';
import ContactsIcon from 'src/images/icons/contacts-nav-icon.svg';
import SettingsIcon from 'src/images/icons/settings-nav-icon.svg';
import BrandingIcon from 'src/images/icons/setting-branding.svg?noAttrs';
import AtsMappingIcon from 'src/images/icons/settings-ats-mapping-icon.svg?noAttrs';
import DataIntegrationIcon from 'src/images/icons/settings-database.svg?noAttrs';
import UsersIcon from 'src/images/icons/settings-users-icon.svg';
import SettingsNumberIcon from 'src/images/icons/settings-number-icon.svg';
import SettingsRolesIcon from 'src/images/icons/settings-roles-icon.svg';
import SettingsGroupsIcon from 'src/images/icons/settings-groups-icon.svg';
import ReferralIcon from 'src/images/icons/referral-icon.svg';
import AnalyticsReferralIcon from 'src/images/icons/referral-analytics-icon.svg';
import SchedulerIcon from 'src/images/icons/calendar-grid.svg';
import NavReferralIcon from 'src/images/icons/referral-nav-icon.svg';
import VoiceflowIcon from 'src/images/icons/voiceflow-nav-icon.svg';
import BlankCalendarIcon from 'src/images/icons/calendar-blank.svg?noAttrs';
import MeetingsAnalyticsIcon from 'src/images/icons/meetings-product.svg';
import CalendarMenuIcon from 'src/images/icons/calendar-menu.svg?noAttrs';
import CalendarStarIcon from 'src/images/icons/calendar-star.svg?noAttrs';
import CalendarAddIcon from 'src/images/icons/calendar-add.svg?noAttrs';
import ContentSubscriptionIcon from 'src/images/icons/content-subscription-icon.svg?noAttrs';
import ClientContactIcon from 'src/images/client-contact.svg?noAttrs';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import TadaIcon from 'src/images/icons/tada.svg?noAttrs';
import ProgramsSettingsIcon from 'src/images/programs-side-bar-icon.svg?noAttrs';
import EventsIcon from 'src/images/icons/events-nav-icon.svg';
import JdIcon from 'src/images/icons/jd-nav-icon.svg';
import CommunicationsIcon from 'src/images/communications-nav-icon.svg';

import PagesIcon from 'src/images/icons/pages-nav-icon.svg';

import css from './side.css';


type Props = {
  className: string | null | undefined;
  location: RouteLocation;
  workflows: {
    [id: string]: Workflow;
  };
  workflow?: Workflow;
  events: Event[] | null | undefined;
  curClient?: string;
  hidden: boolean;
  canViewAnalytics: boolean;
  canViewAudienceMembers: boolean;
  canViewWorkflows: boolean;
  canEditWorkflows: boolean;
  canEditSettings: boolean;
  canViewChat: boolean;
  canViewChatorEditChatbot: boolean;
  analyticsPerformancePageEnabled: boolean;
  analyticsCohortsEnabled: boolean;
  schedulingAllowed: boolean;
  schedulerEnabled: boolean;
  params: Params;
  canViewReferrals: boolean;
  canViewVoiceflow: boolean;
  canViewSurveys: boolean;
};

// @ts-expect-error - TS7031 - Binding element 'pathname' implicitly has an 'any' type.
const collapsePrimaryNavForJourneyRoutes = ({pathname}): boolean =>
  pathname.startsWith(`/journey`);

// @ts-expect-error - TS7031 - Binding element 'pathname' implicitly has an 'any' type.
const collapsePrimaryNavForJourneyJ2Routes = ({pathname}): boolean =>
  pathname.startsWith(`/automation`);

// @ts-expect-error - TS7031 - Binding element 'pathname' implicitly has an 'any' type.
const collapsePrimaryNavForTrmRoutes = ({pathname}): boolean =>
  pathname.startsWith(`/trm`);

// @ts-expect-error - TS7031 - Binding element 'pathname' implicitly has an 'any' type.
const collapsePrimaryNavForJobsRoutes = ({pathname}): boolean =>
  pathname.startsWith(`/jobs`);

const collapsePrimaryNavForPeopleRoutes = ({
  // @ts-expect-error - TS7031 - Binding element 'pathname' implicitly has an 'any' type.
  pathname,
  // @ts-expect-error - TS7031 - Binding element 'audienceFilterSeparationEnabled' implicitly has an 'any' type.
  audienceFilterSeparationEnabled,
}): boolean =>
  audienceFilterSeparationEnabled && pathname.startsWith(`/people`);

// @ts-expect-error - TS7031 - Binding element 'pathname' implicitly has an 'any' type.
const collapsePrimaryNavForReferralv2Routes = ({pathname}): boolean =>
  pathname.startsWith(`/referralv2`);

// @ts-expect-error - TS7031 - Binding element 'pathname' implicitly has an 'any' type.
const collapsePrimaryNavForMeetings = ({pathname}): boolean =>
  pathname.startsWith('/meetings');

export type PredicateFnArgs = {
  pathname: string;
  audienceFilterSeparationEnabled: boolean;
};
const shouldCollapsePrimaryNav = (
  predicateFnArray: Array<(arg1: PredicateFnArgs) => boolean>,
  args: PredicateFnArgs,
) => predicateFnArray.some((predicate) => predicate(args));

const SideNavigation = (props: Props) => {
  const {
    workflows,
    events,
    location,
    hidden,
    canViewAnalytics,
    canViewAudienceMembers,
    canViewWorkflows,
    canEditWorkflows,
    canEditSettings,
    canViewChat,
    canViewChatorEditChatbot,
    analyticsPerformancePageEnabled,
    analyticsCohortsEnabled,
    schedulingAllowed,
    schedulerEnabled,
    params,
    canViewReferrals,
    canViewVoiceflow,
    canViewSurveys,
  } = props;
  const dispatch = useDispatch();
  const {pathname} = location;
  const atsBulkWritebackEnabled = useSelector((state) =>
    selectAgencyConfigFlag(state, 'atsBulkWritebackEnabled'),
  );
  const [showWorkflows, setShowWorkflows] = React.useState(false);
  const audienceFilterSeparationEnabled = useAudienceFilterSeparation();
  const trmEnabled = useTRMEnabled();

  const collapsePrimaryNav = shouldCollapsePrimaryNav(
    [
      collapsePrimaryNavForJourneyRoutes,
      collapsePrimaryNavForJourneyJ2Routes,
      collapsePrimaryNavForTrmRoutes,
      collapsePrimaryNavForPeopleRoutes,
      collapsePrimaryNavForJobsRoutes,
      collapsePrimaryNavForReferralv2Routes,
      collapsePrimaryNavForMeetings,
    ],
    {
      pathname,
      audienceFilterSeparationEnabled,
    },
  );

  React.useEffect(() => {
    setShowWorkflows(false);
  }, [pathname]);

  React.useEffect(() => {
    dispatch(toggleSideNav(collapsePrimaryNav));
  }, [collapsePrimaryNav]);

  const canNavigateToHomepage = useReleaseFlag('journeysHomepage');
  const handleWorkflowClick = React.useCallback(
    (event: React.SyntheticEvent) => {
      if (canNavigateToHomepage) {
        return;
      }
      event.preventDefault();
      setShowWorkflows(!showWorkflows);
    },
    [showWorkflows],
  );

  const handleAnalyticsClick = React.useCallback(
    (event: React.SyntheticEvent) => {
      const analyticsMatches = pathname.match(/^\/analytics$/);
      if (analyticsMatches) {
        setShowWorkflows(false);
      }
    },
    [pathname],
  );

  // TODO (kyle): figure out if all this matching is necessary
  let currentWorkflowId;

  const workflowMatches = pathname.match(/^\/journey\/(\d+)/);
  if (workflowMatches) {
    currentWorkflowId = workflowMatches && workflowMatches[1];
  }

  const analyticsMatches = pathname.match(/^\/analytics/);

  const analyticsWorkflowMatches = pathname.match(
    /^\/analytics\/journey\/(\d+)/,
  );
  if (analyticsWorkflowMatches) {
    currentWorkflowId = analyticsWorkflowMatches[1];
  }

  const hideSupportButton = ['survey', 'messages', 'contacts'].some((path) =>
    pathname.startsWith(`/${path}`),
  );

  const details = [];

  const customerBusinessType =
    useSelector(selectAgencyConfig).customerBusinessType;
  const isCorporate = customerBusinessType === 'corporate';

  if (canViewWorkflows && showWorkflows) {
    details.push(
      <DefaultWorkflowList
        key="workflows"
        workflows={workflows}
        canEdit={canEditWorkflows}
      />,
    );
  } else if (
    canViewAnalytics &&
    ((analyticsMatches && !checkAnalyticsNewLandingPageEnabled()) ||
      pathname === '/analytics' ||
      pathname.includes('/analytics/journey'))
  ) {
    details.push(
      // NOTE (kyle): to keep this component usefully pure, we only pass the pathname when it possibly matches one of its links
      <AnalyticsDetail
        key="analytics"
        workflows={workflows}
        location={location}
        analyticsPerformancePageEnabled={
          analyticsPerformancePageEnabled && !isCorporate
        }
      />,
    );
    if (currentWorkflowId && workflows[currentWorkflowId]) {
      details.push(
        <AnalyticsWorkflowDetail
          key="analytics-events"
          location={location}
          workflow={workflows[currentWorkflowId]}
          events={events}
        />,
      );
    }
    if (analyticsCohortsEnabled && pathname.startsWith('/analytics/cohorts')) {
      details.push(
        <AnalyticsEngagementCohortsDetail
          key="analytics-cohorts"
          // @ts-expect-error - TS2740 - Type 'RerouterLocation' is missing the following properties from type 'Location': ancestorOrigins, host, hostname, origin, and 5 more.
          location={location}
          params={params}
        />,
      );
    }
  } else if (pathname.startsWith('/settings')) {
    details.push(
      // @ts-expect-error - TS2786 - 'SettingsDetail' cannot be used as a JSX component.
      <SettingsDetail
        key="settings-details"
        location={location}
        canViewChat={canViewChat}
        canEditSettings={canEditSettings}
        schedulerEnabled={schedulerEnabled}
      />,
    );
  }

  const shouldExpand = !(canViewChat && pathname.startsWith('/messages'));

  const expand = !details.length && !hidden;

  const content = (
    <div className={!details.length ? css.containerOpen : css.container}>
      <TopNav
        onWorkflowClick={handleWorkflowClick}
        onAnalyticsClick={handleAnalyticsClick}
        expand={expand}
        pathname={pathname}
        showWorkflows={showWorkflows}
        canViewAnalytics={canViewAnalytics}
        canViewAudienceMembers={canViewAudienceMembers}
        canViewWorkflows={canViewWorkflows}
        trmEnabled={trmEnabled}
        canEditSettings={canEditSettings}
        canViewChat={canViewChat}
        canViewChatorEditChatbot={canViewChatorEditChatbot}
        // @ts-expect-error - TS2322 - Type 'number | boolean' is not assignable to type 'boolean'.
        atsBulkWritebackEnabled={atsBulkWritebackEnabled}
        schedulerEnabled={schedulerEnabled}
        schedulingAllowed={schedulingAllowed}
        canViewReferrals={canViewReferrals}
        canViewVoiceflow={canViewVoiceflow}
        shouldExpand={shouldExpand}
        canViewSurveys={canViewSurveys}
      />
      {!hideSupportButton && (
        // @ts-expect-error - TS2769 - No overload matches this call.
        <ZendeskTrigger className={css.zendeskSupportIcon} />
      )}
      {}

      {/* @ts-expect-error - TS2322 - Type '{ children: Element[]; className: string; }' is not assignable to type 'IntrinsicAttributes & { children: ReactElement<{}, string | JSXElementConstructor<any>>[]; }'. */}
      <Pusher className={css.detailContainer}>{details}</Pusher>
    </div>
  );

  const pinClasses = {
    [css.expanded]: expand,
    [css.detailed]: details.length,
  } as const;

  return (
    <ScrollView>
      {({above}) => (
        <div
          className={classify(css.navAnchor, pinClasses, {
            [css.pinned]: !above,
          })}
        >
          {content}
        </div>
      )}
    </ScrollView>
  );
};

export default SideNavigation;

const AnalyticsDetail = ({
  workflows,
  analyticsPerformancePageEnabled,
  location,
}: {
  workflows: {
    [id: string]: Workflow;
  };
  location: RouteLocation;
  analyticsPerformancePageEnabled: boolean;
}) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const {pathname} = location;
  const isChatbotEnabled = useSelector(selectAgencyConfig).enableChatbot;
  // TODO(darsh): once centralization is done ask analytics team to remove voicebot transcripts from API
  const showVoicebotTranscripts =
    useSelector(selectAgencyConfig).voiceAiEnabled;
  const [referralsEnabled, canUseReferral] = useReferrals();
  const analyticsV2Enabled = useAnalyticsV2();
  const schedulerEnabled = useSchedulerEnabled();
  const isDiscoverViewEnabled = useReleaseFlag('discoverViewEnabled');
  const discoverViewEnabled = isInternalUser() || isDiscoverViewEnabled;
  const jobMatchEnabled = useJobMatchEnabledForAnyProduct();
  const landingPageView = useDisplayConfig('landing_page_view');
  const isReferralAnalytics = useAgencyConfig('enableReferralV2');
  // [TODO]: Remove this once we have a better way to handle this - One way is useDisplayConfig
  // Discussed with @dhirah [ Analytics ]
  const checkAccessForEmbedding = useApi<boolean>('/sigma/access', {
    apiPath: ANALYTICS_API_V3,
  });

  const isNewAnalyticsJourneyDashboard =
    pathname === '/analytics' && checkAnalyticsNewLandingPageEnabled();

  const analyticsWorkflowList = (
    <AnalyticsWorkflowList key="analytics" workflows={workflows} isAnalytics />
  );

  return (
    <div className={css.detail}>
      <section className={css.detailSection}>
        <div className={css.detailTitle}>{`${t(
          'Dashboards',
          'Dashboards',
        )}`}</div>
        <div className={css.detailItems}>
          {analyticsPerformancePageEnabled &&
            !isNewAnalyticsJourneyDashboard && (
              <AnalyticsDetailItem
                icon={<PerformanceIcon />}
                to="/analytics/performance"
                data-qa-id="select-performance-analytics"
              >{`${t('Performance', 'Performance')}`}</AnalyticsDetailItem>
            )}
          <AnalyticsDetailItem
            icon={<MetricsIcon />}
            to="/analytics"
            onlyActiveOnIndex
            data-qa-id="select-overall-analytics"
          >
            {analyticsV2Enabled
              ? t('Overview_Metrics', 'Overview Metrics')
              : t('Overall_Metrics', 'Overall Metrics')}
          </AnalyticsDetailItem>
          {!isNewAnalyticsJourneyDashboard && (
            <AnalyticsDetailItem
              icon={<ResponsesIcon />}
              to="/analytics/responses"
              data-qa-id="select-responses-analytics"
            >{`${t('Responses', 'Responses')}`}</AnalyticsDetailItem>
          )}
          {!isNewAnalyticsJourneyDashboard && (
            <AnalyticsDetailItem
              icon={<FlagIcon />}
              to="/analytics/flagged-responses"
              data-qa-id="select-flagged-analytics"
            >{`${t(
              'Flagged_Responses',
              'Flagged Responses',
            )}`}</AnalyticsDetailItem>
          )}
          {analyticsV2Enabled && !isNewAnalyticsJourneyDashboard && (
            <AnalyticsDetailItem
              // @ts-expect-error - TS2322 - Type '{ className: string; }' is not assignable to type 'IntrinsicAttributes'.
              icon={<DeliveryHistoryIcon className={css.deliveryHistory} />}
              to="/analytics/delivery-history"
            >{`${t(
              'Delivery_History',
              'Delivery History',
            )}`}</AnalyticsDetailItem>
          )}
          {!isNewAnalyticsJourneyDashboard && (
            <AnalyticsDetailItem
              icon={<ClicksIcon />}
              to="/analytics/clicks"
              data-qa-id="select-clicks-analytics"
            >{`${t(
              'External_Link_Clicks',
              'External Link Clicks',
            )}`}</AnalyticsDetailItem>
          )}
          {isChatbotEnabled && !isNewAnalyticsJourneyDashboard && (
            <>
              <AnalyticsDetailItem
                icon={<ChatbotAnalyticsIcon />}
                to="/analytics/chatbot"
                data-qa-id="select-chatbot-analytics"
              >{`${t(
                'Chatbot_Metrics',
                'Chatbot Metrics',
              )}`}</AnalyticsDetailItem>
              <AnalyticsDetailItem
                icon={<ChatbotTranscriptsIcon />}
                to="/analytics/transcripts"
                data-qa-id="select-chatbot-transcripts"
              >
                {t('Chatbot_Transcripts', 'Chatbot Transcripts')}
              </AnalyticsDetailItem>
            </>
          )}
          {showVoicebotTranscripts && !isNewAnalyticsJourneyDashboard && (
            <AnalyticsDetailItem
              icon={<ChatbotTranscriptsIcon />}
              to="/analytics/voicebot-transcripts"
              data-qa-id="select-chatbot-transcripts"
            >
              {t('Voicebot_Transcripts', 'Voicebot Transcripts')}
            </AnalyticsDetailItem>
          )}
          {!isNewAnalyticsJourneyDashboard && (
            <AnalyticsDetailItem
              icon={<MessagesAnalyticsIcon />}
              to="/analytics/messages/outbound"
              data-qa-id="select-messages-analytics"
            >{`${t('Messages', 'Messages')}`}</AnalyticsDetailItem>
          )}
          {referralsEnabled &&
            canUseReferral &&
            !isNewAnalyticsJourneyDashboard && (
              <AnalyticsDetailItem
                icon={<AnalyticsReferralIcon />}
                to="/analytics/referral"
                data-qa-id="select-referral-analytics"
              >{`${t('Referrals', 'Referrals')}`}</AnalyticsDetailItem>
            )}
          {schedulerEnabled && !isNewAnalyticsJourneyDashboard && (
            <AnalyticsDetailItem
              icon={<MeetingsAnalyticsIcon />}
              to="/analytics/meetings"
            >{`${t('Meetings', 'Meetings')}`}</AnalyticsDetailItem>
          )}
          {!isNewAnalyticsJourneyDashboard && (
            <AnalyticsDetailItem
              icon={<NpsIcon />}
              to="/analytics/nps"
              data-qa-id="select-nps-analytics"
            >{`${t('NPS', 'NPS')}`}</AnalyticsDetailItem>
          )}
          {jobMatchEnabled &&
            discoverViewEnabled &&
            !isNewAnalyticsJourneyDashboard && (
              <AnalyticsDetailItem
                icon={<JobMatchesIcon />}
                to="/analytics/discover"
                data-qa-id="select-job-match-analytics"
              >{`${t('Job_Matches', 'Job Matches')}`}</AnalyticsDetailItem>
            )}
          {referralsEnabled &&
            canUseReferral &&
            !isNewAnalyticsJourneyDashboard && (
              <AnalyticsDetailItem
                icon={<AnalyticsReferralIcon />}
                to="/analytics/referral"
                data-qa-id="select-referral-analytics"
              >{`${t('Referrals', 'Referrals')}`}</AnalyticsDetailItem>
            )}
        </div>
      </section>
      {checkAnalyticsNewLandingPageEnabled()
        ? isNewAnalyticsJourneyDashboard
          ? analyticsWorkflowList
          : null
        : analyticsWorkflowList}
    </div>
  );
};

const AnalyticsWorkflowDetail = ({
  location,
  workflow,
  events,
}: {
  location: RouteLocation;
  workflow: Workflow;
  events: Event[] | null | undefined;
}) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const analyticsV2Enabled = useAnalyticsV2();
  const isChatbotEnabled = useSelector(selectAgencyConfig).enableChatbot;
  const basePath = '/analytics/journey/' + workflow.id;

  return (
    <div className={css.detail}>
      <Link
        className={css.detailWorkflowTitle}
        to={{
          ...location,
          pathname: `/analytics`,
        }}
      >
        <ArrowLeft className={css.backArrow} />
        <TruncatedText text={workflow.name} limit={25} />
      </Link>

      <section className={css.detailSectionEvents}>
        <div className={css.detailTitleEvents}>{`${t(
          'Dashboards',
          'Dashboards',
        )}`}</div>
        <div className={css.detailItems}>
          <Link
            className={css.detailItemEvents}
            activeClassName={css.detailEventOn}
            onlyActiveOnIndex
            to={{
              ...location,
              pathname: basePath,
            }}
          >
            {/* @ts-expect-error - TS2322 - Type '{ className: string; }' is not assignable to type 'IntrinsicAttributes'. */}
            <MetricsIcon className={css.metricsIcon} />
            {analyticsV2Enabled
              ? t('Overview_Metrics', 'Overview Metrics')
              : t('Metrics', 'Metrics')}
          </Link>

          <EventDetailItem
            icon={<ResponsesIcon />}
            to={basePath + '/responses'}
          >{`${t('Responses', 'Responses')}`}</EventDetailItem>

          <EventDetailItem
            icon={<FlagIcon />}
            to={basePath + '/flagged-responses'}
          >{`${t('Flagged_Responses', 'Flagged Responses')}`}</EventDetailItem>

          {analyticsV2Enabled && (
            <EventDetailItem
              // @ts-expect-error - TS2322 - Type '{ className: string; }' is not assignable to type 'IntrinsicAttributes'.
              icon={<DeliveryHistoryIcon className={css.deliveryHistory} />}
              to={`${basePath}/delivery-history`}
            >{`${t('Delivery_History', 'Delivery History')}`}</EventDetailItem>
          )}

          <EventDetailItem
            icon={<ClicksIcon />}
            to={basePath + '/clicks'}
          >{`${t('Clicks', 'Clicks')}`}</EventDetailItem>

          {isChatbotEnabled && (
            <>
              <EventDetailItem
                icon={<ChatbotAnalyticsIcon />}
                to={basePath + '/chatbot'}
              >{`${t('Chatbot_Metrics', 'Chatbot Metrics')}`}</EventDetailItem>
              <EventDetailItem
                icon={<ChatbotTranscriptsIcon />}
                to={basePath + '/transcripts'}
              >{`${t('Transcripts', 'Transcripts')}`}</EventDetailItem>
            </>
          )}

          <EventDetailItem
            icon={<EngagementIcon />}
            to={basePath + '/engagement'}
          >{`${t('Engagement', 'Engagement')}`}</EventDetailItem>
        </div>
      </section>

      <section className={css.detailSectionEvents}>
        <div className={css.detailTitleEvents}>{`${t(
          'Touchpoints',
          'Touchpoints',
        )}`}</div>
        <div className={css.detailEvents}>
          {events ? (
            events.map((event) => (
              <TruncatedText
                text={event.title}
                limit={25}
                component={Link}
                className={css.detailEvent}
                activeClassName={css.detailEventOn}
                to={{
                  ...location,
                  pathname: `${basePath}/touchpoint/${event.id}`,
                }}
                key={event.id}
              />
            ))
          ) : (
            <Loading />
          )}
        </div>
      </section>
    </div>
  );
};

const _SettingsDetail = ({
  location,
  canViewChat,
  canViewChatDev,
  canEditPermissions,
  canEditSettings,
  schedulerEnabled,
  entities,
  emailDNSV2Enabled,
}: {
  location: RouteLocation;
  canViewChat: boolean;
  canViewChatDev: boolean;
  canEditPermissions: boolean;
  canEditSettings: boolean;
  schedulerEnabled: boolean;
  entities: AtsEntity[];
  emailDNSV2Enabled: boolean;
}) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const [referralsEnabled, canUseReferral] = useReferrals();
  const corporateLabels = useSelector(selectCorporateLabels);
  const audienceFilterSeparationEnabled = useAudienceFilterSeparation();
  const j2Enabled = useJ2Enabled();
  const isChatbotEnabled = useSelector(selectEnableChatbot);
  const hasChatbotPermissions = usePrivilege('EDIT_CHATBOT');
  const hasMessagingPermissions = usePrivilege('USE_MESSAGING');
  const viewCredentialsPrivilege = usePrivilege('VIEW_CREDENTIALS');
  const editCredentialsPrivilege = usePrivilege('EDIT_CREDENTIALS');

  const canManageSecurity = usePrivilege('MANAGE_SECURITY');
  const textTemplatesEnabled = useReleaseFlag('messagingTemplatesTable');
  const useGenesisUIForAuthentication = useReleaseFlag(
    'genesisUiForAuthentication',
  );
  const credentialsUIEnabled = useReleaseFlag('dataCredentialsUiEnabled');
  const credentialMetaData = useSelector(selectCredentialsMetaData);
  const credentials = useSelector(selectCredentials);
  const hasArnieEntityCredGroupInMeta =
    // @ts-expect-error - TS2345 - Argument of type 'CredentialsConfig | null' is not assignable to parameter of type 'CredentialsConfig'.
    hasArnieEntityCredGroup(credentialMetaData);
  const hasArnieEntityCredGroupInCredentials =
    // @ts-expect-error - TS2345 - Argument of type 'CredentialsConfig | null' is not assignable to parameter of type 'CredentialsConfig'.
    hasArnieEntityCredGroup(credentials);

  const showDataIntegrationTab = hasArnieEntityCredGroupInMeta
    ? hasArnieEntityCredGroupInCredentials
    : true;

  const isHolidayManagementEnabled = useSchedulerHolidyManagement();
  const seesHolidayManagementLink =
    isHolidayManagementEnabled && canEditSettings && schedulerEnabled;
  const isTRMCustomer = useDisplayConfig('is_trm_customer'); // Only showing "Accounts" section for TRM customers
  const agencyConfig = useSelector(selectAgencyConfig);

  const showEntity = (name: EntityType) => {
    if (
      name === 'ae_client_company_employee' &&
      agencyConfig.customerBusinessType === 'corporate'
    ) {
      // Similarly need to hard code contact entity type of all other ATSs, this is not a good approach. Should be handled from the backend.
      return false;
    }
    return true;
  };

  // FIXME(marcos): check here for chat+messaging permissions as well
  const hasLATPermissions =
    hasMessagingPermissions && isChatbotEnabled && hasChatbotPermissions;
  const seesLATQueueSettings = useLiveAgentTransfer() && hasLATPermissions;

  const leadEntities = entities.filter(({is_lead_entity}) => is_lead_entity);
  const atsEntities = entities.filter(({is_lead_entity}) => !is_lead_entity);

  return (
    <div className={css.detail}>
      {!isTRMCustomer && (
        <section className={css.detailSection}>
          <div className={css.detailTitle}>{`${t(
            'Settings',
            'Settings',
          )}`}</div>
          <div className={css.detailItems}>
            {canEditSettings && (
              <>
                <Link
                  className={css.detailItem}
                  activeClassName={css.detailItemOn}
                  onlyActiveOnIndex={true}
                  to={{
                    ...location,
                    pathname: '/settings',
                  }}
                >
                  <SettingsIcon className={css.settingsDetailIcon} />
                  {`${t('General', 'General')}`}
                </Link>
                {j2Enabled && (
                  <Link
                    className={css.detailItem}
                    activeClassName={css.detailItemOn}
                    onlyActiveOnIndex={true}
                    to={{
                      ...location,
                      pathname: '/settings/blackout-windows',
                    }}
                  >
                    <CommunicationsIcon className={css.settingsDetailIcon} />
                    {`${t('Communication_Windows', 'Communication Windows')}`}
                  </Link>
                )}
                <Link
                  className={css.detailItem}
                  activeClassName={css.detailItemOn}
                  onlyActiveOnIndex={true}
                  to={{
                    ...location,
                    pathname: '/settings/brand',
                  }}
                >
                  {/* @ts-expect-error - TS2322 - Type '{ className: string; }' is not assignable to type 'IntrinsicAttributes'. */}
                  <BrandingIcon className={css.settingsDetailIcon} />
                  {`${t('Branding', 'Branding')}`}
                </Link>
                {audienceFilterSeparationEnabled && (
                  <Link
                    className={css.detailItem}
                    activeClassName={css.detailItemOn}
                    onlyActiveOnIndex={true}
                    to={{
                      ...location,
                      pathname: '/settings/ats-mapping',
                    }}
                  >
                    {/* @ts-expect-error - TS2322 - Type '{ className: string; }' is not assignable to type 'IntrinsicAttributes'. */}
                    <AtsMappingIcon className={css.settingsDetailIcon} />
                    {`${t('ATS_Mapping', 'ATS Mapping')}`}
                  </Link>
                )}
                {(viewCredentialsPrivilege || editCredentialsPrivilege) &&
                  credentialsUIEnabled &&
                  showDataIntegrationTab && (
                    <Link
                      className={css.detailItem}
                      activeClassName={css.detailItemOn}
                      onlyActiveOnIndex={true}
                      to={{
                        ...location,
                        pathname: '/settings/data-integration',
                      }}
                    >
                      {/* @ts-expect-error - TS2322 - Type '{ className: string; }' is not assignable to type 'IntrinsicAttributes'. */}
                      <DataIntegrationIcon className={css.settingsDetailIcon} />
                      {t('Data_Integration', 'Data Integration')}
                    </Link>
                  )}
                <Link
                  className={css.detailItem}
                  activeClassName={css.detailItemOn}
                  to={{
                    ...location,
                    pathname: '/settings/templates',
                  }}
                >
                  {/* @ts-expect-error - TS2322 - Type '{ className: string; }' is not assignable to type 'IntrinsicAttributes'. */}
                  <TemplateIcon className={css.settingsDetailIcon} />
                  {labelI18nInstance.t(
                    corporateLabels['email_templates'],
                    'Templates',
                  )}
                </Link>
              </>
            )}

            {textTemplatesEnabled && (
              <Link
                className={css.detailItem}
                activeClassName={css.detailItemOn}
                to={{
                  ...location,
                  pathname: '/settings/text-templates',
                }}
              >
                {/* @ts-expect-error - TS2322 - Type '{ className: string; }' is not assignable to type 'IntrinsicAttributes'. */}
                <SmartphoneIcon className={css.settingsDetailIcon} />
                {`${t('Text_Templates', 'Text Templates')}`}
              </Link>
            )}
            {canEditSettings && (
              <>
                <Link
                  className={css.detailItem}
                  activeClassName={css.detailItemOn}
                  to={{
                    ...location,
                    pathname: '/settings/global-variables',
                  }}
                >
                  {/* @ts-expect-error - TS2322 - Type '{ className: string; }' is not assignable to type 'IntrinsicAttributes'. */}
                  <GlobalVariableIcon className={css.settingsDetailIcon} />
                  {`${t('Global_Variables', 'Global Variables')}`}
                </Link>
                <Link
                  className={css.detailItem}
                  activeClassName={css.detailItemOn}
                  to={{
                    ...location,
                    pathname: '/settings/flagged-words',
                  }}
                  data-qa-id="flagging"
                >
                  {/* @ts-expect-error - TS2322 - Type '{ className: string; }' is not assignable to type 'IntrinsicAttributes'. */}
                  <FlagIcon className={css.settingsDetailIcon} />
                  {`${t('Flagging', 'Flagging')}`}
                </Link>
                {canViewChatDev && canViewChat && (
                  <Link
                    className={css.detailItem}
                    activeClassName={css.detailItemOn}
                    to={{
                      ...location,
                      pathname: '/settings/phones',
                    }}
                  >
                    {/* @ts-expect-error - TS2322 - Type '{ className: string; }' is not assignable to type 'IntrinsicAttributes'. */}
                    <PhoneIcon className={css.settingsDetailIcon} />
                    {`${t('Phone_Numbers', 'Phone Numbers')}`}
                  </Link>
                )}
                {agencyConfig.customerBusinessType !== 'corporate' && (
                  <Link
                    className={css.detailItem}
                    activeClassName={css.detailItemOn}
                    to={{
                      ...location,
                      pathname: '/settings/ats/performance',
                    }}
                  >
                    {/* @ts-expect-error - TS2322 - Type '{ className: string; }' is not assignable to type 'IntrinsicAttributes'. */}
                    <PerformanceIcon className={css.performanceIcon} />
                    {`${t('Performance', 'Performance')}`}
                  </Link>
                )}

                {emailDNSV2Enabled && (
                  <Link
                    className={css.detailItem}
                    activeClassName={css.detailItemOn}
                    to={{
                      ...location,
                      pathname: '/settings/email-dns',
                    }}
                  >
                    {/* @ts-expect-error - TS2322 - Type '{ className: string; }' is not assignable to type 'IntrinsicAttributes'. */}
                    <EmailIcon className={css.settingsDetailIcon} />
                    {`${t('Email_DNS', 'Email DNS')}`}
                  </Link>
                )}
              </>
            )}
            <Link
              className={css.detailItem}
              activeClassName={css.detailItemOn}
              to={{
                ...location,
                pathname: '/settings/reports',
              }}
            >
              {/* @ts-expect-error - TS2322 - Type '{ className: string; }' is not assignable to type 'IntrinsicAttributes'. */}
              <ReportsIcon className={css.settingsDetailIcon} />
              {`${t('Reports', 'Reports')}`}
            </Link>
            {canEditSettings && (
              <Link
                className={css.detailItem}
                activeClassName={css.detailItemOn}
                to={{
                  ...location,
                  pathname: '/settings/content-subscription/category',
                }}
              >
                {/* @ts-expect-error - TS2322 - Type '{ className: string; }' is not assignable to type 'IntrinsicAttributes'. */}
                <ContentSubscriptionIcon className={css.settingsDetailIcon} />
                {`${t(
                  'User_Content_Subscription',
                  'User Content Subscription',
                )}`}
              </Link>
            )}
            {referralsEnabled && canUseReferral && (
              <Link
                className={css.detailItem}
                activeClassName={css.detailItemOn}
                to={{
                  ...location,
                  pathname: '/settings/referrals',
                }}
              >
                {/* @ts-expect-error - TS2322 - Type '{ className: string; }' is not assignable to type 'IntrinsicAttributes'. */}
                <ReferralGlyphIcon className={css.settingsDetailIcon} />
                {`${t('Referrals', 'Referrals')}`}
              </Link>
            )}
            {canEditSettings && schedulerEnabled && (
              <Link
                className={css.detailItem}
                activeClassName={css.detailItemOn}
                to={{...location, pathname: '/settings/calendar-sync'}}
              >
                {/* @ts-expect-error - TS2322 - Type '{ className: string; }' is not assignable to type 'IntrinsicAttributes'. */}
                <BlankCalendarIcon className={css.settingsDetailIcon} />
                {`${t('Calendar_Sync', 'Calendar Sync')}`}
              </Link>
            )}
            {seesHolidayManagementLink && (
              <Link
                className={css.detailItem}
                activeClassName={css.detailItemOn}
                to={{...location, pathname: '/settings/holidays'}}
              >
                {/* @ts-expect-error - TS2322 - Type '{ className: string; }' is not assignable to type 'IntrinsicAttributes'. */}
                <TadaIcon className={css.settingsDetailIcon} />
                {`${t('Holidays', 'Holidays')}`}
              </Link>
            )}

            {canEditSettings && seesLATQueueSettings && (
              <Link
                className={css.detailItem}
                activeClassName={css.detailItemOn}
                to={{...location, pathname: '/settings/live-agent-transfer'}}
              >
                <Icon
                  className={css.settingsDetailIcon}
                  color="secondary"
                  name="headset"
                  size="medium"
                  type="regular"
                />
                {`${t('Live_Chat', 'Live Chat')}`}
              </Link>
            )}
          </div>
        </section>
      )}

      {canEditSettings && (
        <>
          <section className={css.detailSection}>
            <div className={css.detailTitle}>{`${t(
              'Accounts',
              'Accounts',
            )}`}</div>
            <div className={css.detailItems}>
              <Link
                className={css.detailItem}
                activeClassName={css.detailItemOn}
                to={{
                  ...location,
                  pathname: '/settings/accounts',
                  query: {...location.query, search: ''},
                }}
              >
                <UsersIcon className={css.settingsDetailIcon} />
                {`${t('Users', 'Users')}`}
              </Link>
              <Link
                className={css.detailItem}
                activeClassName={css.detailItemOn}
                to={{
                  ...location,
                  pathname: '/settings/numbers',
                  query: {...location.query, search: ''},
                }}
              >
                <SettingsNumberIcon className={css.settingsDetailIcon} />
                {`${t('Numbers', 'Numbers')}`}
              </Link>
              {canEditPermissions && (
                <React.Fragment>
                  <Link
                    className={css.detailItem}
                    activeClassName={css.detailItemOn}
                    to={{
                      ...location,
                      pathname: '/settings/roles',
                    }}
                  >
                    <SettingsRolesIcon className={css.settingsDetailIcon} />
                    {`${t('Roles', 'Roles')}`}
                  </Link>
                  <Link
                    className={css.detailItem}
                    activeClassName={css.detailItemOn}
                    to={{
                      ...location,
                      pathname: '/settings/groups',
                    }}
                    data-qa-id="groups-setting-link"
                  >
                    <SettingsGroupsIcon className={css.settingsDetailIcon} />
                    {`${t('Groups', 'Groups')}`}
                  </Link>
                  {useGenesisUIForAuthentication && canManageSecurity && (
                    <Link
                      className={css.detailItem}
                      activeClassName={css.detailItemOn}
                      to={{
                        ...location,
                        pathname: '/settings/security',
                      }}
                    >
                      <ProgramsSettingsIcon
                        // @ts-expect-error - TS2322 - Type '{ className: string; }' is not assignable to type 'IntrinsicAttributes'.
                        className={css.settingsDetailIcon}
                      />
                      {`${t('Security', 'Security')}`}
                    </Link>
                  )}
                  {!isTRMCustomer && (
                    <Link
                      className={css.detailItem}
                      activeClassName={css.detailItemOn}
                      to={{
                        ...location,
                        pathname: '/settings/communication-preference',
                      }}
                    >
                      {/* @ts-expect-error - TS2322 - Type '{ className: string; }' is not assignable to type 'IntrinsicAttributes'. */}
                      <ClientContactIcon className={css.settingsDetailIcon} />
                      {`${t(
                        'Communication_Preferences',
                        'Communication Preferences',
                      )}`}
                    </Link>
                  )}
                </React.Fragment>
              )}
            </div>
          </section>
          {canEditPermissions && !isTRMCustomer && (
            <TearsheetsSettingsLink
              location={location}
              canEditPermissions={canEditPermissions}
            />
          )}
          {!isTRMCustomer && (
            <section className={css.detailSection}>
              <div className={css.detailTitle}>{t('Leads', 'Leads')}</div>
              {leadEntities.map(
                ({name, display_name}) =>
                  showEntity(name) && (
                    <Link
                      key={name}
                      className={css.detailItem}
                      activeClassName={css.detailItemOn}
                      to={{
                        ...location,
                        pathname: `/settings/ats/fields/${name}`,
                      }}
                    >
                      {/* @ts-expect-error - TS2322 - Type '{ className: string; }' is not assignable to type 'IntrinsicAttributes'. */}
                      <AtsFieldsIcon className={css.settingsDetailIcon} />
                      {titleCase(display_name)}
                    </Link>
                  ),
              )}
            </section>
          )}
          {!isTRMCustomer && (
            <section className={css.detailSection}>
              <div className={css.detailTitle}>
                {t('ATS_Fields', 'ATS Fields')}
              </div>
              {atsEntities.map(
                ({name, display_name}) =>
                  showEntity(name) && (
                    <Link
                      className={css.detailItem}
                      activeClassName={css.detailItemOn}
                      to={{
                        ...location,
                        pathname: `/settings/ats/fields/${name}`,
                      }}
                    >
                      {/* @ts-expect-error - TS2322 - Type '{ className: string; }' is not assignable to type 'IntrinsicAttributes'. */}
                      <AtsFieldsIcon className={css.settingsDetailIcon} />
                      {titleCase(display_name)}
                    </Link>
                  ),
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
};

const TearsheetsSettingsLink = ({
  location,
  canEditPermissions,
}: {
  location: RouteLocation;
  canEditPermissions: boolean;
}) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const dispatch = useDispatch();

  React.useEffect(() => {
    canEditPermissions && dispatch(getAllowedTearsheetTypes());
  }, [canEditPermissions]);

  const allowedTearsheetTypes: AllowedTearsheetTypes = useSelector(
    (state) => state.atsSettings.allowedTearsheetTypes,
  );

  return (
    allowedTearsheetTypes?.length !== 0 && (
      <section className={css.detailSection}>
        <div className={css.detailTitle}>{`${t(
          'ATS_Syncs',
          'ATS Syncs',
        )}`}</div>
        <div className={css.detailItems}>
          <Link
            className={css.detailItem}
            activeClassName={css.detailItemOn}
            key="tearsheets"
            to={{
              ...location,
              pathname: '/settings/ats/tearsheets',
            }}
          >
            {/* @ts-expect-error - TS2322 - Type '{ className: string; }' is not assignable to type 'IntrinsicAttributes'. */}
            <TemplateIcon className={css.settingsDetailIcon} />
            {`${t('Tearsheets', 'Tearsheets')}`}
          </Link>
        </div>
      </section>
    )
  );
};

const SettingsDetail = connect((state) => ({
  canEditPermissions: hasPrivilege(state, EDIT_PERMISSIONS),
  canViewChatDev: canUseChatPreRelease(state),
  entities: getAtsEntityMappings(state),
  emailDNSV2Enabled: selectDNSV2Enabled(state),
}))(_SettingsDetail);

function DetailItem({
  children,
  className,
  activeClassName = css.detailItemOn,
  icon,
  ...props
}: React.ComponentProps<typeof Link> & {
  icon: React.ReactNode;
}) {
  return (
    <Link
      className={className || css.detailItem}
      activeClassName={activeClassName}
      {...props}
    >
      <div className={css.detailIcon}>{icon}</div>
      {children}
    </Link>
  );
}

// @ts-expect-error - TS7031 - Binding element 'to' implicitly has an 'any' type.
function AnalyticsDetailItem({to, ...props}) {
  return (
    // @ts-expect-error - TS2741 - Property 'icon' is missing in type '{ to: (location: RerouterLocation) => { pathname: any; href: string; search: string; searchParams: URLSearchParams; query: Query; hash: string; state: unknown; }; }' but required in type '{ icon: ReactNode; }'.
    <DetailItem {...props} to={(location) => ({...location, pathname: to})} />
  );
}

// @ts-expect-error - TS7006 - Parameter 'props' implicitly has an 'any' type.
function EventDetailItem(props) {
  return (
    <AnalyticsDetailItem
      {...props}
      className={css.detailItemEvents}
      activeClassName={css.detailEventOn}
    />
  );
}

function TopNav(props: {
  onWorkflowClick: (arg1: React.SyntheticEvent) => unknown;
  onAnalyticsClick: (arg1: React.SyntheticEvent) => unknown;
  expand?: unknown;
  // NOTE (kyle): below props needed for lifecylce hooks
  pathname: string;
  showWorkflows?: unknown;
  canViewAnalytics: boolean;
  canViewAudienceMembers: boolean;
  canViewWorkflows: boolean;
  trmEnabled: boolean;
  canEditSettings: boolean;
  canViewChat: boolean;
  canViewChatorEditChatbot: boolean;
  atsBulkWritebackEnabled: boolean;
  schedulerEnabled: boolean;
  schedulingAllowed: boolean;
  canViewReferrals: boolean;
  canViewVoiceflow: boolean;
  shouldExpand: boolean;
  canViewSurveys: boolean;
}) {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const {
    onWorkflowClick,
    onAnalyticsClick,
    expand,
    canViewAnalytics,
    canViewAudienceMembers,
    canViewWorkflows,
    trmEnabled,
    canEditSettings,
    canViewChat,
    canViewChatorEditChatbot,
    atsBulkWritebackEnabled,
    schedulerEnabled,
    schedulingAllowed,
    canViewReferrals,
    canViewVoiceflow,
    shouldExpand,
    canViewSurveys,
  } = props;

  const [stateExpand, setExpand] = React.useState(false);
  const corporateLabels = useSelector(selectCorporateLabels);

  const showVoiceFlowMarketingLandingPage = useVoiceAiMarketingPageEnabled();

  const isTRMCustomer = useDisplayConfig('is_trm_customer');

  React.useEffect(() => {
    setExpand(false);
  }, [props.pathname, props.showWorkflows]);

  const handleMouseEnter = () => {
    setExpand(true);
  };

  const handleMouseLeave = () => {
    setExpand(false);
  };

  const getSettingsLink = () => {
    if (isTRMCustomer) {
      return '/settings/accounts';
    } else {
      return canEditSettings ? '/settings' : '/settings/reports';
    }
  };

  const isChatbotEnabled = useChatbotEnabled();
  const showSenseEvents = useSenseEvents();
  const isChatbotAllowed = usePrivilege('EDIT_CHATBOT');
  const [referralsEnabled, canUseReferral] = useReferrals();
  const referralV2 = useAgencyConfig('enableReferralV2');
  const analyticsDefaultPageEnabled = useReleaseFlag(
    'analyticsDefaultPageEnabled',
  );
  const isNewCrmUiEnabled = useReleaseFlag('crmRedesignEnabled');

  const landingPageView = useDisplayConfig('landing_page_view');
  const jobsListEnabled = useDisplayConfig('jobs_list_enabled');
  const canViewAutomationWorkflows = useDisplayConfig('aep_workflow_view');
  const viewJobsPrivilege = usePrivilege('VIEW_JOBS');
  const adminJobsPrivilege = usePrivilege('ADMIN_JOBS');

  const j2Enabled = useJ2Enabled();

  const isExpanded = expand || (shouldExpand && stateExpand);
  const customerBusinessType =
    useSelector(selectAgencyConfig).customerBusinessType;
  const isCorporate = customerBusinessType === 'corporate';

  const isPageHaveViewPrivilege = usePrivilege('VIEW_PAGES');
  const isPageHaveAdminPrivilege = usePrivilege('ADMIN_PAGES');
  const isPageHaveEditPrivilege = usePrivilege('EDIT_PAGES');

  const automationSurveyEnabled = useAutomationSurvey();
  const aiRecruiterEnabled = useAgencyConfig('aiRecruiterEnabled');
  const aiRecruiterV2Enabled = useAgencyConfig('aiRecruiterV2Enabled');
  const viewAiRecruiter = usePrivilege('VIEW_AI_RECRUITER');

  const pagessHaveNoAccess =
    isPageHaveViewPrivilege ||
    isPageHaveAdminPrivilege ||
    isPageHaveEditPrivilege;

  return (
    <div className={expand ? css.topNavContainerExpand : css.topNavContainer}>
      <div
        className={isExpanded ? css.topNavExpand : css.topNav}
        onMouseLeave={handleMouseLeave}
        onMouseEnter={handleMouseEnter}
      >
        {canViewAnalytics && (
          <TopNavLink
            to="/dashboard"
            icon={<DashboardIcon className={css.topNavIcon} />}
            label={`${t('Dashboard', 'Dashboard')}`}
            expanded={isExpanded}
          />
        )}
        {(canViewWorkflows || canViewAutomationWorkflows) && (
          <TopNavLink
            to={!canViewWorkflows ? '/automation-workflows' : '/journeys'}
            icon={<JourneysIcon className={css.topNavIcon} />}
            label={
              j2Enabled
                ? t('Automation', 'Automation')
                : t('Journeys', 'Journeys')
            }
            expanded={isExpanded}
            onClick={onWorkflowClick}
          />
        )}
        {automationSurveyEnabled && canViewSurveys && (
          <TopNavLink
            to="/automation-surveys"
            icon={<SurveysIcon className={css.topNavIcon} />}
            label="Surveys"
            expanded={isExpanded}
          />
        )}
        {canViewAutomationWorkflows &&
          aiRecruiterEnabled &&
          viewAiRecruiter && (
            <TopNavLink
              to="/ai-recruiter"
              icon={<AiRecruiterIcon className={css.topNavIconAir} />}
              label={t('AI_Recruiter', 'AI Recruiter')}
              expanded={isExpanded}
            />
          )}
        {canViewAutomationWorkflows &&
          aiRecruiterV2Enabled &&
          viewAiRecruiter && (
            <TopNavLink
              to="/ai-recruiter-v2"
              icon={<AiRecruiterIcon className={css.topNavIconAir} />}
              label={t('AI_Recruiter_v2', 'AI Recruiter v2')}
              expanded={isExpanded}
            />
          )}
        {trmEnabled && (
          <TopNavLink
            to="/trm"
            icon={<TrmIcon className={css.topNavIcon} />}
            label={t('TRM', 'TRM')}
            expanded={isExpanded}
          />
        )}
        {canViewWorkflows && atsBulkWritebackEnabled && (
          <TopNavLink
            to="/database-cleanup"
            icon={<CleanupIcon className={css.topNavIcon} />}
            label={t('Bulk_Cleanup', 'Bulk Cleanup')}
            expanded={isExpanded}
          />
        )}
        {(canViewAnalytics || canViewChat) && (
          <>
            {!canViewAnalytics && canViewChat ? (
              <TopNavLink
                to="/analytics/messages/outbound"
                icon={<AnalyticsIcon className={css.topNavIcon} />}
                label={`${t('Analytics', 'Analytics')}`}
                expanded={isExpanded}
              />
            ) : (
              <TopNavLink
                to={
                  checkAnalyticsNewLandingPageEnabled()
                    ? '/reports'
                    : !isCorporate && analyticsDefaultPageEnabled
                    ? '/analytics/performance'
                    : '/analytics'
                }
                icon={<AnalyticsIcon className={css.topNavIcon} />}
                label={`${t('Analytics', 'Analytics')}`}
                expanded={isExpanded}
                onClick={onAnalyticsClick}
              />
            )}
          </>
        )}
        {checkJdGeneratorEnabled() && (
          <TopNavLink
            to="/sense-jd-ai"
            icon={<JdIcon className={css.topNavIcon} />}
            label={`${t('Jd_generator', 'Jd generator')}`}
            expanded={isExpanded}
          />
        )}
        {canViewAnalytics && atsBulkWritebackEnabled && (
          <TopNavLink
            to="/audit"
            icon={<AuditIcon className={css.topNavIcon} />}
            label={`${t('Audit', 'Audit')}`}
            expanded={isExpanded}
          />
        )}
        {referralsEnabled && canUseReferral && (
          <TopNavLink
            to="/referral"
            icon={<ReferralIcon className={css.topNavIcon} />}
            label={`${t('Referral', 'Referral')}`}
            expanded={isExpanded}
          />
        )}
        {showSenseEvents && (
          <TopNavLink
            to="/drive"
            icon={<EventsIcon className={css.topNavIcon} />}
            label={`${t('Events', 'Events')}`}
            expanded={isExpanded}
          />
        )}
        {canViewAnalytics && (
          <TopNavLink
            to="/schedule"
            icon={<ScheduleIcon className={css.topNavIcon} />}
            label={`${t('Timeline', 'Timeline')}`}
            expanded={isExpanded}
          />
        )}
        {canViewAudienceMembers && (
          <TopNavLink
            to="/people"
            icon={<PeopleIcon className={css.topNavIcon} />}
            label={`${t('People', 'People')}`}
            expanded={isExpanded}
          />
        )}

        {(jobsListEnabled || viewJobsPrivilege || adminJobsPrivilege) && (
          <TopNavLink
            to="/jobs"
            icon={<JobsIcon className={css.topNavIcon} />}
            label={`${t('Jobs', 'Jobs')}`}
            expanded={isExpanded}
          />
        )}

        {/* Enable before releasing to prod @swatantra */}
        {trmEnabled && isNewCrmUiEnabled && (
          <TopNavLink
            to="/crm"
            icon={<JobsIcon className={css.topNavIcon} />}
            label={`${t('CRM_Jobs', 'CRM Jobs')}`}
            expanded={isExpanded}
          />
        )}

        {trmEnabled && isNewCrmUiEnabled && (
          <TopNavLink
            to="/crm/candidates"
            icon={<PeopleIcon className={css.topNavIcon} />}
            label={t('Candidates', 'Candidates')}
            expanded={isExpanded}
          />
        )}

        {isChatbotEnabled && isChatbotAllowed && (
          <TopNavLink
            to="/conversations"
            icon={<ChatbotIcon className={css.topNavIcon} />}
            label={`${t('Conversations', 'Conversations')}`}
            expanded={isExpanded}
          />
        )}
        {canViewChatorEditChatbot && (
          // @ts-expect-error - TS2786 - 'MessagesNavLink' cannot be used as a JSX component.
          <MessagesNavLink
            // @ts-expect-error - TS2322 - Type '{ to: string; icon: Element; label: string; expanded: {}; }' is not assignable to type 'IntrinsicAttributes & Omit<JSX.LibraryManagedAttributes<C, GetProps<C>>, string | number | symbol>'.
            to="/messages"
            icon={<MessagesIcon className={css.topNavIcon} />}
            label={`${t('Messages', 'Messages')}`}
            expanded={isExpanded}
          />
        )}
        {(canViewVoiceflow || showVoiceFlowMarketingLandingPage) && (
          <TopNavLink
            to={
              showVoiceFlowMarketingLandingPage
                ? '/voiceflow/landing-page'
                : '/voiceflow'
            }
            icon={<VoiceflowIcon className={css.topNavIcon} />}
            label={`${t('Voiceflows', 'Voiceflows')}`}
            expanded={isExpanded}
          />
        )}
        {!!referralV2 && canViewReferrals && (
          <TopNavLink
            to="/referralv2"
            icon={<NavReferralIcon className={css.topNavIcon} />}
            label={`${t('Referrals', 'Referrals')}`}
            expanded={isExpanded}
          />
        )}
        {landingPageView && pagessHaveNoAccess && (
          <TopNavLink
            to="/pages/overview"
            icon={<PagesIcon className={css.topNavIcon} />}
            label={`${t('PAGES', 'PAGES')}`}
            expanded={isExpanded}
          />
        )}
        {canViewChat && (
          <TopNavLink
            to="/contacts"
            icon={<ContactsIcon className={css.topNavIcon} />}
            label={labelI18nInstance.t(
              corporateLabels['sense_contacts'],
              'Contacts',
            )}
            expanded={isExpanded}
          />
        )}

        {schedulerEnabled && schedulingAllowed && (
          <TopNavLink
            to={'/meetings'}
            icon={<SchedulerIcon className={css.topNavIcon} />}
            label={`${t('Meetings', 'Meetings')}`}
            expanded={isExpanded}
          />
        )}
        <TopNavLink
          to={getSettingsLink()}
          icon={<SettingsIcon className={css.topNavIcon} />}
          label={`${t('Settings', 'Settings')}`}
          data-qa-id="select-settings-page"
          expanded={isExpanded}
        />
      </div>
    </div>
  );
}

// @ts-expect-error - TS7031 - Binding element 'location' implicitly has an 'any' type.
function SchedulerDetail({location}) {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const isTeamTemplater = usePrivilege('MANAGE_TEAM_TEMPLATES');
  const showScheduledEvents = useReleaseFlag('schedulerEnableScheduledEvents');
  return (
    <div className={css.detail}>
      <section className={css.detailSection}>
        <div className={css.detailTitle}>{`${t('Meetings', 'Meetings')}`}</div>
        <div className={css.detailItems}>
          {showScheduledEvents && (
            <Link
              className={css.detailItem}
              activeClassName={css.detailItemOn}
              onlyActiveOnIndex={true}
              to={{
                ...location,
                pathname: '/meetings/events',
              }}
            >
              {/* @ts-expect-error - TS2322 - Type '{ className: string; }' is not assignable to type 'IntrinsicAttributes'. */}
              <CalendarStarIcon className={css.settingsDetailIcon} />
              {`${t('Scheduled_Events', 'Scheduled Events')}`}
            </Link>
          )}
          <Link
            className={css.detailItem}
            activeClassName={css.detailItemOn}
            onlyActiveOnIndex={true}
            to={{
              ...location,
              pathname: '/meetings/template',
            }}
          >
            {/* @ts-expect-error - TS2322 - Type '{ className: string; }' is not assignable to type 'IntrinsicAttributes'. */}
            <CalendarMenuIcon className={css.settingsDetailIcon} />
            {`${t('My_Templates', 'My Templates')}`}
          </Link>
          {isTeamTemplater && (
            <Link
              className={css.detailItem}
              activeClassName={css.detailItemOn}
              to={{
                ...location,
                pathname: '/meetings/team',
              }}
            >
              {/* @ts-expect-error - TS2322 - Type '{ className: string; }' is not assignable to type 'IntrinsicAttributes'. */}
              <CalendarAddIcon className={css.settingsDetailIcon} />
              {`${t('Team_Templates', 'Team Templates')}`}
            </Link>
          )}
          <Link
            className={css.detailItem}
            activeClassName={css.detailItemOn}
            onlyActiveOnIndex={true}
            to={{
              ...location,
              pathname: '/meetings/settings',
            }}
          >
            <SettingsIcon className={css.settingsDetailIcon} />
            {`${t('Settings', 'Settings')}`}
          </Link>
        </div>
      </section>
    </div>
  );
}
